// Yes — the conversational-iMessage engine admin surface (gated: sec-yes).
//
// READ + CONFIG only. This page surfaces what the yes-engine is doing (the
// yes.* conversation-outcome funnel by state, the pre-claim skip reasons, the
// by-campaign / by-tier breakdowns, and a recent-conversations list) and lets an
// operator edit its operating config + flip it on/off. It never touches the
// engine's dispatch / exclusive-gate / claim / reply logic — those live in
// rtb-api (yes_dispatch.ts + yes_inbound.ts) and are structural (the exclusive
// gate fails CLOSED; the once-per-lead claim is a phone-keyed conditional write;
// the reply classifier is a deterministic pure function). The engine is a
// scheduled sibling of the dialer / lead-sale proxy; this page is a near-exact
// clone of LeadSales.tsx with a campaigns-array editor in place of the
// destinations / routing / exclusive editors.
//
// Two data planes, deliberately separate:
//   - LIVE (stats + recent conversations): windowed, auto-refreshed every 15s
//     (the Dialer / Lead-Sales poll pattern; pauses while the tab is hidden). The
//     window picker defaults to "Today (PT)" — passing no window lets the server
//     use its Pacific-midnight default (api.yesStats()).
//   - CONFIG (the yes_config singleton): loaded once, mutated via the master
//     on/off toggle and the config form.
//
// 🔒 Live-safety: turning Yes ON starts REAL outbound iMessaging of leads, so the
// master toggle requires a deliberate confirm step to enable (Off is immediate).
// The timing band (wait/max_age) sits under an "Advanced" disclosure with a
// client-side guard (max_age must exceed wait), mirroring the server's 400
// invalid_band. msgs_per_min is capped at the dispatcher's per-tick ceiling.
// `enabled` is owned by the master toggle, never a config Save (a stale form
// can't re-enable a killed engine).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import {
  api,
  ApiError,
  type YesCampaign,
  type YesConfig,
  type YesConfigResponse,
  type YesConversationRow,
  type YesLlmMode,
  type YesOutcomeCounts,
  type YesStats,
  type YesTdPostCounts,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  type PillTone,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const POLL_MS = 15_000;
const CONVERSATIONS_LIMIT = 100;
// Fallback per-tick intro ceiling shown before the config (with the real
// tick_cap) loads. msgs_per_min may rise to the cap, never past it.
const TICK_CAP_FALLBACK = 60;

// "Today (PT)" is the default selected window and a special sentinel: it sends
// NO window param so the server applies its Pacific-midnight-to-now default
// (api.yesStats() with no args). The other presets are rolling-window seconds.
const TODAY_PT = -1;

const PRESETS: Array<{ label: string; sec: number }> = [
  { label: "Today (PT)", sec: TODAY_PT },
  { label: "Last 15 min", sec: 15 * 60 },
  { label: "Last 30 min", sec: 30 * 60 },
  { label: "Last hour", sec: 3600 },
  { label: "Last 3 hours", sec: 3 * 3600 },
  { label: "Last 6 hours", sec: 6 * 3600 },
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

// The window params for api.yesStats — an explicit epoch range, a rolling window
// (seconds), or `undefined` (→ server Pacific-Today default). Matches the
// server's precedence: start/end wins, else window, else the default.
type StatsOpts = { windowSec?: number; start?: number; end?: number };

/** Resolve the selected preset/custom into api.yesStats opts. */
function toStatsOpts(
  presetSec: number,
  custom: { start: string; end: string } | null,
): StatsOpts | undefined {
  if (custom && custom.start && custom.end) {
    const start = Math.floor(new Date(custom.start + "T00:00:00Z").getTime() / 1000);
    const end = Math.floor(new Date(custom.end + "T23:59:59Z").getTime() / 1000);
    return { start, end };
  }
  if (presetSec === TODAY_PT) return undefined; // server default = Pacific Today
  return { windowSec: presetSec };
}

function fmtCount(n: number): string {
  return (n ?? 0).toLocaleString();
}

/** A 0..1 rate as a percentage string. Coarse above 10%, one decimal below it (a 2%
 *  delivery rate must not round to "2%" next to a 2.4% one and look identical). */
function fmtPct(rate: number): string {
  if (!Number.isFinite(rate)) return "—";
  const pct = rate * 100;
  return `${pct === 0 || pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%`;
}

// Render a conversation timestamp (epoch ms) in Pacific, matching the PT
// reporting model used across the dashboards. Nullable (reply time can be unset).
function fmtTime(ms: number | null): string {
  if (!ms) return "—";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleString([], {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }) + " PT"
  );
}

// A stored tier code → operator-facing label. Unknown values pass through so the
// table never blanks on an unexpected tier.
function prettyTier(tier: string): string {
  switch (tier) {
    case "over_20k":
      return "Over $20k";
    case "over_10k":
      return "Over $10k";
    case "below_10k":
      return "Under $10k";
    default:
      return tier || "—";
  }
}

// A by_sender bucket key → operator-facing label. The number is OURS (not consumer
// PII) so it is shown UNMASKED, verbatim; only the server's catch-all bucket is
// relabelled.
function prettySender(sender: string): string {
  if (sender === "unknown") return "Unknown (no sender recorded)";
  return sender || "—";
}

/** Total conversations attributed to one sender = the SUM of its state buckets.
 *  A row's state EVOLVES after the intro lands (intro_sent → converted / opted_out /
 *  replied_other …), so `intro_sent` alone would badly undercount how much a number
 *  actually carried. `from_number` is only recorded once Bloo resolved a sender —
 *  i.e. once a send really happened — so the bucket total IS the send count. */
function sumOutcomes(o: YesOutcomeCounts): number {
  return Object.values(o).reduce((a, b) => a + b, 0);
}

// A stored conversation state → operator-facing label. Unknown values pass
// through so the table never blanks on an unexpected state.
function prettyState(state: string): string {
  switch (state) {
    case "intro_sending":
      return "Intro sending";
    case "intro_sent":
      return "Intro sent";
    case "converted":
      // 🔴 A REPLY IS NOT A CONVERSION (operator, 2026-07-25). This state means the
      // lead answered affirmatively and we sent the callback DID — nothing more. The
      // only real outcome this engine has is a CALL (see the "Calls from iMessage"
      // card), which is measured from the Arkade TrackDrive account, not from a reply.
      return "Replied yes";
    case "opted_out":
      return "Opted out";
    case "replied_other":
      return "Replied (other)";
    case "undeliverable":
      return "Undeliverable";
    case "send_failed":
      return "Send failed";
    case "send_deferred":
      return "Deferred — will retry";
    case "followup_failed":
      return "Follow-up failed";
    case "number_pending":
      // v5 — replied yes, follow-up sent, the bare number is scheduled on the
      // dispatcher tick. A WAIT state, not an outcome; it normally clears in ~a
      // minute (or after the campaign's configured delay).
      return "Number scheduled";
    case "expired":
      return "Expired";
    default:
      return state || "—";
  }
}

// Map a conversation state to a Pill tone. `converted` is the AFFIRMATIVE-REPLY
// outcome — the lead said yes and got the DID; it is NOT a conversion (a CALL is
// the only real outcome). Opt-outs warn; delivery failures are danger.
// 🔴 `send_deferred` is checked BEFORE the generic fail/undeliverable test: it is a
// released claim that WILL be retried, not a lost lead, so it warns — it must never
// be coloured (or read) as a failure.
function stateTone(state: string): PillTone {
  const s = state.toLowerCase();
  if (s === "converted") return "success";
  if (s === "send_deferred") return "warning";
  if (s === "opted_out") return "warning";
  if (s === "undeliverable" || s.includes("fail")) return "danger";
  return "neutral"; // intro_sending / intro_sent / replied_other / expired / unknown
}

// The headline outcome stat cards (from stats.outcomes). `converted` is the
// affirmative-REPLY outcome (the DID was sent) — NOT a conversion; the emphasised
// endpoint is "Calls from iMessage". 🔴 `send_deferred` gets its OWN card,
// framed as a retry (not a failure): a deferral released the lead's claim and the
// engine retries it while it is still inside the band, so lumping it in with
// "Send failed" would read as lost leads that are not lost.
const OUTCOME_CARDS: Array<{
  key: keyof YesOutcomeCounts;
  label: string;
  hint: string;
  emphasis?: boolean;
}> = [
  // 🔴 LABEL FIX (v3): this figure has NEVER been a delivery number — `intro_sent` means
  // Bloo ACCEPTED the send. The old hint claimed delivery, which was misleading even
  // before v3 and is now plainly wrong: a REAL delivery figure sits next to it
  // (stats.delivered, from Bloo's receipts). So this card says exactly what it is.
  { key: "intro_sent", label: "Intros sent", hint: "accepted by Bloo" },
  {
    key: "converted",
    label: "Replied yes",
    hint: "replied yes → follow-up sent",
    emphasis: true,
  },
  { key: "opted_out", label: "Opted out", hint: "replied STOP" },
  { key: "replied_other", label: "Other replies", hint: "replied, not yes/stop" },
  { key: "undeliverable", label: "Undeliverable", hint: "carrier rejected" },
  { key: "send_failed", label: "Send failed", hint: "transport error" },
  {
    key: "send_deferred",
    label: "Deferred — will retry",
    hint: "send refused (capacity/blip) — lead released, retried in the window",
  },
];

// Friendly labels for the full outcome funnel — ALL TEN conversation states, so a
// ledger row can never exist in a state this panel cannot show. `send_deferred`
// carries a WARNING tone (pending retry), deliberately not danger.
const FUNNEL_LABELS: Array<{
  key: keyof YesOutcomeCounts;
  label: string;
  tone?: PillTone;
}> = [
  { key: "intro_sending", label: "Intro sending" },
  { key: "intro_sent", label: "Intro sent" },
  { key: "converted", label: "Replied yes", tone: "success" },
  { key: "opted_out", label: "Opted out", tone: "warning" },
  { key: "replied_other", label: "Replied (other)" },
  { key: "undeliverable", label: "Undeliverable", tone: "danger" },
  { key: "send_failed", label: "Send failed", tone: "danger" },
  { key: "send_deferred", label: "Deferred — will retry", tone: "warning" },
  { key: "followup_failed", label: "Follow-up failed", tone: "danger" },
  // v5 — the 11th state: a WAIT (the tick sends the number shortly), not an
  // outcome. A large standing count = the dispatcher's step pass is not running.
  { key: "number_pending", label: "Number scheduled" },
  { key: "expired", label: "Expired" },
];

// 🔴 THE CONVERSATION STATES THAT PROVE BLOO ACCEPTED A SEND — the denominator of the
// delivery rate, and the "accepted" figure the by-sender panel shows. `send_failed` /
// `send_deferred` / `intro_sending` are EXCLUDED (no message was ever produced for
// them); `undeliverable` IS included (Bloo accepted it; the carrier rejected it after).
// Mirror of the server's MESSAGED_STATES (routes/yes.ts) — copied, not imported.
const MESSAGED_STATE_KEYS: Array<keyof YesOutcomeCounts> = [
  "intro_sent",
  "converted",
  "opted_out",
  "replied_other",
  "followup_failed",
  "expired",
  "undeliverable",
  // v5: intro AND follow-up were both accepted for a number_pending row.
  "number_pending",
];

/** How many conversations Bloo ACCEPTED a send for (see MESSAGED_STATE_KEYS). */
function acceptedTotal(o: YesOutcomeCounts): number {
  let n = 0;
  for (const k of MESSAGED_STATE_KEYS) n += o[k] ?? 0;
  return n;
}

// The TrackDrive lead-POST outcome breakdown. Failures are visually DISTINCT (danger)
// and rejections warn — this card is the observability that makes a best-effort
// side-effect safe: a 100% rejection rate looks perfectly healthy from the messaging
// side and is visible ONLY here.
//
// 🔴 "Rejected — DNC" is INFORMATIONAL, not a withheld lead: those consumers WERE
// messaged. There is no suppression anywhere in this engine (TrackDrive's DNC list
// governs outbound DIALLING, which is the dialer's concern, not iMessage). The card's
// hint says so, so nobody reads the row as "held back".
const TD_POST_LABELS: Array<{
  key: keyof YesTdPostCounts;
  label: string;
  tone?: PillTone;
}> = [
  { key: "posted", label: "Posted", tone: "success" },
  { key: "existing_lead", label: "Already had it", tone: "success" },
  { key: "rejected_dnc", label: "Rejected — DNC (still messaged)", tone: "warning" },
  { key: "rejected_dupe", label: "Rejected — duplicate", tone: "warning" },
  { key: "rejected_other", label: "Rejected — other", tone: "warning" },
  { key: "failed", label: "Failed", tone: "danger" },
  { key: "invalid_phone", label: "Invalid phone", tone: "danger" },
  { key: "post_error", label: "Bookkeeping error", tone: "danger" },
  { key: "not_posted", label: "Not posted / not recorded" },
];

const SKIP_LABELS: Array<{ key: keyof YesStats["skips"]; label: string }> = [
  { key: "invalid_phone", label: "Invalid phone" },
  { key: "quiet_hours", label: "Quiet hours" },
  { key: "no_debt", label: "No debt on file" },
  { key: "no_campaign", label: "No matching campaign" },
  { key: "exclusive_check_failed", label: "Exclusive check failed (fail-closed)" },
  { key: "blocked_exclusive", label: "Blocked — exclusive buyer" },
  { key: "already_contacted", label: "Already contacted" },
  { key: "lead_error", label: "Lead error" },
  {
    key: "consecutive_send_failures",
    label: "Send breaker tripped (upstream failing)",
  },
];

// The merge tags both copy fields support. Same WORDING as rtb-api's
// domain/yes_tags.ts:YES_TAG_HINT — copied, not imported (admin-web cannot import
// rtb-api), so if the renderer's vocabulary changes, change it here too.
const YES_TAG_HINT =
  "{n} = first name · {p} = callback number ({first_name} also works)";

// ---------------------------------------------------------------------------
// Config form state. Numeric fields (rates, minutes, hours, dollar thresholds)
// are kept as strings so the inputs can be cleared mid-edit; they're parsed +
// validated on Save. The keyword lists are kept as raw comma/newline text and
// parsed on Save. `enabled` is NOT here — the master toggle owns it; Save carries
// the current enabled through. Each campaign has a STABLE id (the ledger's
// campaign_id + the by-campaign stats key on it); a new draft mints one.
// ---------------------------------------------------------------------------

/** v5: one editable step row (strings for mid-edit clearing; `key` is a
 *  session-stable React key, never persisted). */
interface StepDraft {
  key: string;
  copy: string;
  delayMin: string; // minutes in the UI; ms on the wire
}

interface CampaignDraft {
  id: string;
  name: string;
  enabled: boolean;
  minDebt: string; // dollar threshold (string for mid-edit clearing)
  intro_copy: string;
  followup_copy: string;
  // 🔴 v4: SENT AS ITS OWN MESSAGE (message 3) right after the follow-up, verbatim —
  // and still the {p} merge tag's source. OPTIONAL ("" ⇒ the key is omitted on Save,
  // and there is then no message 3).
  callbackNumber: string;
  // v5: timed pre-reply nudges (≤ 5). Empty ⇒ the key is omitted on Save (v4).
  steps: StepDraft[];
  // v5: minutes between the follow-up and the bare number. "" or "0" ⇒ omitted
  // on Save = v4's immediate send.
  numberDelayMin: string;
}

interface FormState {
  waitMin: string;
  maxAgeMin: string;
  msgsPerMin: string;
  fromNumber: string;
  priorityId: string;
  channelType: string;
  ttlHours: string;
  affirmativeKeywords: string; // comma/newline editable
  stopKeywords: string; // comma/newline editable
  campaigns: CampaignDraft[];
  /** v3, the ONE new knob: post the lead to TrackDrive just before the intro. Default
   *  ON. 🔴 Always sent on Save (the server requires it). No DNC knob exists. */
  trackdrivePostEnabled: boolean;
  /** v5: the Conversation AI ladder (off | shadow | classify). 🔴 Always sent on
   *  Save (the server requires it — an omitted mode is how a stored one would
   *  silently reset). "draft" is not a member of this build. */
  llmMode: YesLlmMode;
}

/** Short, session-stable id for a new campaign row. */
function shortId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function newCampaignDraft(): CampaignDraft {
  return {
    id: shortId(),
    name: "",
    enabled: true,
    minDebt: "",
    intro_copy: "",
    followup_copy: "",
    callbackNumber: "",
    steps: [],
    numberDelayMin: "",
  };
}

/** v5: a fresh, empty step row (1 min is the flow David specced — a sensible seed). */
function newStepDraft(): StepDraft {
  return { key: shortId(), copy: "", delayMin: "1" };
}

/** Parse a comma/newline keyword list to its non-empty, lowercased members. The
 *  inbound classifier compares a lowercased-normalised reply against these, so
 *  lowercasing here keeps a fat-fingered "YES" from silently never matching. */
function parseKeywords(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((k) => k.trim().toLowerCase())
    .filter((k) => k.length > 0);
}

function keywordsToText(list: string[]): string {
  return list.join(", ");
}

function configToForm(c: YesConfig): FormState {
  return {
    waitMin: String(c.wait_ms / 60000),
    maxAgeMin: String(c.max_age_ms / 60000),
    msgsPerMin: String(c.msgs_per_min),
    fromNumber: c.from_number ?? "",
    priorityId: c.priority_id ?? "",
    channelType: c.channel_type ?? "",
    ttlHours: String(c.conversation_ttl_ms / 3_600_000),
    affirmativeKeywords: keywordsToText(c.affirmative_keywords),
    stopKeywords: keywordsToText(c.stop_keywords),
    trackdrivePostEnabled: c.trackdrive_post_enabled,
    llmMode: c.llm_mode,
    campaigns: c.campaigns.map((cm) => ({
      id: cm.id,
      name: cm.name,
      enabled: cm.enabled,
      minDebt: String(cm.min_debt_threshold),
      intro_copy: cm.intro_copy,
      followup_copy: cm.followup_copy,
      callbackNumber: cm.callback_number ?? "",
      steps: (cm.pre_reply_steps ?? []).map((s) => ({
        key: shortId(),
        copy: s.copy,
        delayMin: String(s.delay_ms / 60000),
      })),
      numberDelayMin:
        cm.number_delay_ms !== undefined ? String(cm.number_delay_ms / 60000) : "",
    })),
  };
}

/** Parse + validate the form into a clean YesConfig, or return a message.
 *  `enabled` is carried through (the master toggle owns it). Mirrors the server's
 *  guards so most 400s are prevented client-side (band + campaign fields). The
 *  optional sender-routing strings are set ONLY when non-empty
 *  (exactOptionalPropertyTypes forbids assigning a literal `undefined`). */
function buildConfigFromForm(
  form: FormState,
  enabled: boolean,
  tickCap: number,
): { config: YesConfig } | { error: string } {
  const mpm = Number(form.msgsPerMin);
  if (form.msgsPerMin.trim() === "" || !Number.isFinite(mpm) || mpm <= 0) {
    return { error: "Messages/min must be a positive number." };
  }
  if (mpm > tickCap) {
    return { error: `Messages/min is capped at ${tickCap}.` };
  }
  const wMin = Number(form.waitMin);
  const aMin = Number(form.maxAgeMin);
  if (form.waitMin.trim() === "" || !Number.isFinite(wMin) || wMin <= 0) {
    return { error: "Wait must be a positive number of minutes." };
  }
  if (form.maxAgeMin.trim() === "" || !Number.isFinite(aMin) || aMin <= 0) {
    return { error: "Max age must be a positive number of minutes." };
  }
  const wait_ms = Math.round(wMin * 60000);
  const max_age_ms = Math.round(aMin * 60000);
  if (max_age_ms <= wait_ms) {
    return {
      error:
        "Max age must be greater than wait — otherwise the eligibility band is empty and the engine messages nobody.",
    };
  }
  const ttlHours = Number(form.ttlHours);
  if (form.ttlHours.trim() === "" || !Number.isFinite(ttlHours) || ttlHours <= 0) {
    return { error: "Conversation TTL must be a positive number of hours." };
  }
  const conversation_ttl_ms = Math.round(ttlHours * 3_600_000);

  const affirmative_keywords = parseKeywords(form.affirmativeKeywords);
  const stop_keywords = parseKeywords(form.stopKeywords);

  // Campaigns — every text field required; the dollar threshold must be a
  // non-negative finite number (the server rejects NaN / negative).
  const campaigns: YesCampaign[] = [];
  for (const [i, c] of form.campaigns.entries()) {
    const label = c.name.trim() || `#${i + 1}`;
    if (c.name.trim() === "") return { error: `Campaign ${label}: name is required.` };
    if (c.intro_copy.trim() === "") {
      return { error: `Campaign ${label}: intro copy is required.` };
    }
    if (c.followup_copy.trim() === "") {
      return { error: `Campaign ${label}: follow-up copy is required.` };
    }
    const minDebt = Number(c.minDebt);
    if (c.minDebt.trim() === "" || !Number.isFinite(minDebt) || minDebt < 0) {
      return {
        error: `Campaign ${label}: minimum debt must be zero or a positive number.`,
      };
    }
    const campaign: YesCampaign = {
      id: c.id,
      name: c.name.trim(),
      enabled: c.enabled,
      min_debt_threshold: minDebt,
      intro_copy: c.intro_copy,
      followup_copy: c.followup_copy,
    };
    // OPTIONAL: the key is OMITTED when blank — never sent as "" (the server's
    // schema rejects an empty string, and exactOptionalPropertyTypes forbids
    // assigning a literal `undefined`).
    const callbackNumber = c.callbackNumber.trim();
    if (callbackNumber) campaign.callback_number = callbackNumber;
    // v5 — the timed steps. Mirrors the server's stepSchema (copy required,
    // delay > 0, ≤ 5 steps) so the operator sees the mistake pre-round-trip.
    if (c.steps.length > 5) {
      return { error: `Campaign ${label}: at most 5 steps.` };
    }
    let horizonMs = 0;
    if (c.steps.length > 0) {
      const steps = [];
      for (const [j, s] of c.steps.entries()) {
        if (s.copy.trim() === "") {
          return { error: `Campaign ${label}: step ${j + 1} needs copy.` };
        }
        const dMin = Number(s.delayMin);
        if (s.delayMin.trim() === "" || !Number.isFinite(dMin) || dMin <= 0) {
          return {
            error: `Campaign ${label}: step ${j + 1} wait must be a positive number of minutes.`,
          };
        }
        const delay_ms = Math.round(dMin * 60000);
        horizonMs += delay_ms;
        steps.push({ copy: s.copy, delay_ms });
      }
      campaign.pre_reply_steps = steps;
    }
    // v5 — the number delay. "" or 0 ⇒ omitted (v4's immediate send).
    const ndMin = Number(c.numberDelayMin);
    if (c.numberDelayMin.trim() !== "" && (!Number.isFinite(ndMin) || ndMin < 0)) {
      return {
        error: `Campaign ${label}: number delay must be zero or a positive number of minutes.`,
      };
    }
    if (c.numberDelayMin.trim() !== "" && ndMin > 0) {
      campaign.number_delay_ms = Math.round(ndMin * 60000);
      horizonMs += campaign.number_delay_ms;
    }
    // Mirror of the server's 400 invalid_sequence (20h horizon).
    if (horizonMs > 20 * 60 * 60 * 1000) {
      return {
        error: `Campaign ${label}: the step waits plus the number delay must total 20 hours or less.`,
      };
    }
    campaigns.push(campaign);
  }

  const config: YesConfig = {
    enabled,
    wait_ms,
    max_age_ms,
    msgs_per_min: mpm,
    affirmative_keywords,
    stop_keywords,
    conversation_ttl_ms,
    campaigns,
    // 🔴 ALWAYS emitted (never conditional): the server's schema requires it, and an
    // omitted boolean would silently reset a deliberate "stop posting" to the ON default.
    trackdrive_post_enabled: form.trackdrivePostEnabled,
    // 🔴 v5: ALWAYS emitted, same reasoning — an omitted mode is how a stored
    // "shadow"/"classify" would silently reset to "off".
    llm_mode: form.llmMode,
  };
  // 🔴 Bloo's routing selectors are MUTUALLY EXCLUSIVE — mirror of the server's 400
  // invalid_routing, caught here so the operator sees it before the round-trip.
  // Leaving both blank is the recommended (Bloo-native) setting.
  const fromNumber = form.fromNumber.trim();
  const priorityId = form.priorityId.trim();
  if (fromNumber && priorityId) {
    return {
      error:
        "From number and priority id are mutually exclusive — clear one (or both, which lets Bloo pick and rotate the number itself).",
    };
  }
  if (fromNumber) config.from_number = fromNumber;
  if (priorityId) config.priority_id = priorityId;
  const channelType = form.channelType.trim();
  if (channelType) config.channel_type = channelType;

  return { config };
}

/** Map a config-save failure to a friendly message. The yes route surfaces
 *  invalid_band / invalid_routing / invalid_body as the ApiError `error` code; fall
 *  back to the raw message otherwise. */
function friendlyConfigError(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.body && typeof err.body === "object" && "error" in err.body
        ? (err.body as { error?: unknown }).error
        : undefined;
    if (code === "invalid_band") {
      return "Max age must be greater than wait — the eligibility band is empty as entered.";
    }
    if (code === "invalid_routing") {
      return "From number and priority id are mutually exclusive — clear one (or both, which lets Bloo pick and rotate the number itself).";
    }
    if (code === "invalid_sequence") {
      return "A campaign's step waits plus its number delay must total 20 hours or less.";
    }
    if (code === "invalid_body") {
      return "Some config fields are invalid — check the campaigns, rates, keywords, and timing.";
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Save failed";
}

export default function Yes() {
  // Config plane (the yes_config singleton + the intro tick cap).
  const [config, setConfig] = useState<YesConfigResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Master on/off toggle state (drives setYesEnabled).
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [confirmingEnable, setConfirmingEnable] = useState(false);

  // Live plane (stats + recent conversations), windowed + polled.
  const [presetSec, setPresetSec] = useState<number>(TODAY_PT); // default: Today (PT)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null);
  const [stats, setStats] = useState<YesStats | null>(null);
  const [conversations, setConversations] = useState<YesConversationRow[] | null>(
    null,
  );
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [auto, setAuto] = useState(true);

  const statsOpts = useMemo(() => toStatsOpts(presetSec, custom), [presetSec, custom]);

  // --- Config load ---------------------------------------------------------
  const loadConfig = useCallback(() => {
    setConfigLoading(true);
    api
      .yesConfig()
      .then((r) => {
        // Guard the shape so an edge/API error that fell through to HTML shows a
        // clean message instead of a cryptic undefined-read (the Dialer fix).
        if (!r || typeof r.config !== "object" || r.config === null) {
          throw new Error("Unexpected yes config response — try reloading.");
        }
        setConfig(r);
        setForm(configToForm(r.config));
        setConfigError(null);
        setDirty(false);
      })
      .catch((e: unknown) =>
        setConfigError(e instanceof Error ? e.message : "Failed to load config"),
      )
      .finally(() => setConfigLoading(false));
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // --- Live load (stats + recent conversations) ----------------------------
  const load = useCallback(
    (showSpinner: boolean) => {
      if (showSpinner) setLiveLoading(true);
      Promise.all([api.yesStats(statsOpts), api.yesConversations(CONVERSATIONS_LIMIT)])
        .then(([s, rows]) => {
          setStats(s);
          setConversations(rows);
          setLiveError(null);
          setRefreshedAt(new Date());
        })
        .catch((err: unknown) => {
          setLiveError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLiveLoading(false));
    },
    [statsOpts],
  );

  // A ref so the poll timer always calls the latest fetch (current window)
  // without re-arming the interval on every filter change.
  const fetchRef = useRef<() => void>(() => {});
  useEffect(() => {
    fetchRef.current = () => load(false);
  }, [load]);

  // Re-fetch (with spinner) whenever the window changes.
  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-refresh polling — pauses when the tab is hidden or auto is toggled off.
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchRef.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  // --- Master on/off toggle ------------------------------------------------
  // Off is immediate; On requires the inline confirm (setConfirmingEnable) first.
  const setEnabled = useCallback((next: boolean) => {
    setToggling(true);
    setToggleError(null);
    api
      .setYesEnabled(next)
      .then((cfg) => {
        setConfig((prev) =>
          prev ? { ...prev, config: cfg } : { config: cfg, tick_cap: TICK_CAP_FALLBACK },
        );
        setConfirmingEnable(false);
      })
      .catch((e: unknown) =>
        setToggleError(e instanceof Error ? e.message : "Failed to update"),
      )
      .finally(() => setToggling(false));
  }, []);

  // --- Config form ---------------------------------------------------------
  const update = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((f) => (f ? { ...f, [key]: value } : f));
      setDirty(true);
    },
    [],
  );

  const tickCap = config?.tick_cap ?? TICK_CAP_FALLBACK;

  // Live band guard (mirrors the server's 400 invalid_band): max_age must exceed
  // wait. Only flags when both parse — an empty field is caught by save() instead.
  const waitNum = form ? Number(form.waitMin) : NaN;
  const maxAgeNum = form ? Number(form.maxAgeMin) : NaN;
  const bandInvalid =
    Number.isFinite(waitNum) && Number.isFinite(maxAgeNum) && maxAgeNum <= waitNum;

  // Live routing guard (mirrors the server's 400 invalid_routing): from_number and
  // priority_id are mutually exclusive Bloo selectors.
  const routingConflict = Boolean(
    form && form.fromNumber.trim() && form.priorityId.trim(),
  );

  const save = useCallback(() => {
    if (!form || !config) return;
    const built = buildConfigFromForm(form, config.config.enabled, tickCap);
    if ("error" in built) {
      setConfigError(built.error);
      return;
    }
    setSaving(true);
    api
      .putYesConfig(built.config)
      .then((cfg) => {
        setConfig((prev) =>
          prev ? { ...prev, config: cfg } : { config: cfg, tick_cap: TICK_CAP_FALLBACK },
        );
        setForm(configToForm(cfg));
        setConfigError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) => setConfigError(friendlyConfigError(e)))
      .finally(() => setSaving(false));
  }, [form, config, tickCap]);

  const enabled = config?.config.enabled ?? false;
  const outcomes = stats?.outcomes;
  const skips = stats?.skips;

  // ── v3 headline figures: accepted → DELIVERED → CALLED ────────────────────
  // `accepted` is the delivery rate's denominator (the sends Bloo accepted), which is
  // NOT the intro_sent card alone: a lead who replied is `converted`, but its intro was
  // still sent.
  const accepted = outcomes ? acceptedTotal(outcomes) : 0;
  const deliveredCount = stats?.delivered ?? 0;
  // 🔴 "—", not "0%", when NOTHING was accepted: a rate over a zero denominator is
  // undefined, not zero. The server returns 0 there only to avoid NaN — the UI must not
  // present that as "0% delivered".
  const deliveredHint =
    accepted === 0
      ? "Bloo delivery receipts · — of intros accepted"
      : `Bloo delivery receipts · ${fmtPct(stats?.delivery_rate ?? 0)} of intros accepted`;
  const callsStatus = stats?.calls.status ?? "ok";
  const imsgTagged = stats?.calls.imsg_tagged ?? 0;
  // 🔴 NEVER render an "unavailable" read as a confirmed 0 — the number is MISSING, not
  // zero, and an operator reading 0 would conclude nobody called.
  const callsValue =
    callsStatus === "unavailable" ? "—" : fmtCount(stats?.calls.attributed ?? 0);
  const callsHint =
    callsStatus === "unavailable"
      ? "unavailable — the TrackDrive read failed (not a zero)"
      : callsStatus === "partial"
        ? `partial — pagination bound hit · ${fmtCount(imsgTagged)} tagged imsg`
        : `called the number we texted them · ${fmtCount(imsgTagged)} tagged imsg`;

  // The headline cards, in funnel order. Delivered + Calls are spliced in immediately
  // after "Intros sent" so the accepted → delivered → called drop reads left-to-right.
  const statCards = outcomes
    ? OUTCOME_CARDS.flatMap((c) => {
        const card = {
          key: String(c.key),
          label: c.label,
          value: fmtCount(outcomes[c.key]),
          hint: c.hint,
          emphasis: c.emphasis === true,
        };
        if (c.key !== "intro_sent") return [card];
        return [
          card,
          {
            key: "delivered",
            label: "Delivered",
            value: fmtCount(deliveredCount),
            hint: deliveredHint,
            emphasis: false,
          },
          {
            key: "calls",
            label: "Calls from iMessage",
            value: callsValue,
            hint: callsHint,
            // The funnel's real endpoint — a call is the outcome this engine exists for.
            emphasis: true,
          },
        ];
      })
    : [];

  // Join map: campaign_id → name (from the SAVED config the live engine uses).
  const campaignNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of config?.config.campaigns ?? []) m.set(c.id, c.name);
    return m;
  }, [config]);

  // Health hint — the "dark/idle/live" line under the master toggle.
  let healthHint: string;
  if (!config) {
    healthHint = "Loading yes-engine status…";
  } else if (!enabled) {
    healthHint = "Yes is OFF — no intros are being sent.";
  } else if ((outcomes?.intro_sent ?? 0) === 0 && (outcomes?.intro_sending ?? 0) === 0) {
    healthHint = "Yes is ON but idle — no intros sent in this window yet.";
  } else {
    healthHint =
      "Yes is ON — messaging qualified leads and routing yes-replies to a callback.";
  }

  const presetLabel = custom
    ? "Custom range"
    : PRESETS.find((p) => p.sec === presetSec)?.label ?? "Window";

  const selectStyle: CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  return (
    <div>
      <PageHeader
        title="Yes"
        subtitle="Conversational iMessage — intro → yes-reply → callback, with per-tier campaigns"
        actions={
          <div
            style={{
              display: "flex",
              gap: tokens.space.sm,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={custom ? "custom" : String(presetSec)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  const today = new Date().toISOString().slice(0, 10);
                  setCustom({ start: today, end: today });
                } else {
                  setCustom(null);
                  setPresetSec(Number(e.target.value));
                }
              }}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={String(p.sec)}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <>
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={selectStyle}
                  aria-label="Start date"
                />
                <span style={{ color: tokens.color.fg2 }}>→</span>
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={selectStyle}
                  aria-label="End date"
                />
              </>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
              />
              Auto
            </label>
            <button
              type="button"
              onClick={() => load(true)}
              disabled={liveLoading}
              style={buttonStyle("ghost", liveLoading)}
            >
              {liveLoading ? "…" : "Refresh"}
            </button>
          </div>
        }
      />

      {refreshedAt && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginBottom: tokens.space.md,
          }}
        >
          Updated{" "}
          {refreshedAt.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
          {auto ? ` · auto-refresh ${POLL_MS / 1000}s` : ""}
          {" · "}
          {presetLabel}
        </div>
      )}

      {/* Master on/off toggle — prominent, confirm-on-enable. */}
      <MasterToggle
        loading={configLoading && !config}
        enabled={enabled}
        healthHint={healthHint}
        toggling={toggling}
        confirming={confirmingEnable}
        toggleError={toggleError}
        onRequestEnable={() => setConfirmingEnable(true)}
        onConfirmEnable={() => setEnabled(true)}
        onCancelEnable={() => setConfirmingEnable(false)}
        onDisable={() => setEnabled(false)}
      />

      {liveError && !stats && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <EmptyState title="Failed to load stats" hint={liveError} />
        </Card>
      )}

      {/* Outcome stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        {liveLoading && !stats ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          statCards.map((c) => (
            <StatCard
              key={c.key}
              label={c.label}
              value={c.value}
              hint={c.hint}
              {...(c.emphasis ? { emphasis: true } : {})}
            />
          ))
        )}
      </div>

      {/* Funnel / skips / by-campaign / by-tier breakdowns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        <Card title="Outcome funnel">
          {liveLoading && !outcomes ? (
            <Skeleton height={200} />
          ) : outcomes ? (
            <Breakdown
              rows={FUNNEL_LABELS.map(({ key, label, tone }) => ({
                label,
                value: outcomes[key],
                ...(tone ? { tone } : {}),
              }))}
            />
          ) : (
            <EmptyState title="No conversation outcomes in this window" />
          )}
        </Card>

        <Card title="Skip reasons">
          {liveLoading && !skips ? (
            <Skeleton height={200} />
          ) : skips ? (
            <Breakdown
              rows={SKIP_LABELS.map(({ key, label }) => ({ label, value: skips[key] }))}
            />
          ) : (
            <EmptyState title="No skips in this window" />
          )}
        </Card>

        <Card title="Replied yes by campaign">
          {liveLoading && !stats ? (
            <Skeleton height={120} />
          ) : stats && stats.by_campaign.length > 0 ? (
            <Breakdown
              rows={stats.by_campaign.map((c) => ({
                label: campaignNameById.get(c.campaign_id) ?? c.campaign_id,
                value: c.outcomes.converted,
                tone: "success" as PillTone,
              }))}
            />
          ) : (
            <EmptyState title="No conversions by campaign in this window" />
          )}
        </Card>

        <Card title="Conversions by tier">
          {liveLoading && !stats ? (
            <Skeleton height={120} />
          ) : stats && stats.by_tier.length > 0 ? (
            <Breakdown
              rows={stats.by_tier.map((t) => ({
                label: prettyTier(t.tier),
                value: t.outcomes.converted,
                tone: "success" as PillTone,
              }))}
            />
          ) : (
            <EmptyState title="No conversions by tier in this window" />
          )}
        </Card>

        {/* 🔴 TrackDrive post — the observability that makes a BEST-EFFORT side-effect
            safe. The lead POST cannot block, defer or fail a message, which means a
            total upstream rejection (e.g. an unregistered traffic source) looks
            perfectly healthy from the messaging side and is visible ONLY here.
            Failures are danger-toned; rejections warn. */}
        <Card title="TrackDrive post">
          {liveLoading && !stats ? (
            <Skeleton height={200} />
          ) : stats ? (
            <>
              <Breakdown
                rows={TD_POST_LABELS.map(({ key, label, tone }) => ({
                  label,
                  value: stats.td_post[key],
                  ...(tone ? { tone } : {}),
                }))}
              />
              <div
                style={{
                  color: tokens.color.fg2,
                  fontSize: tokens.font.size.xs,
                  marginTop: tokens.space.sm,
                }}
              >
                Best-effort: TrackDrive gets the lead just before the intro so it knows
                the caller, and a failure or rejection never blocks the message.{" "}
                <strong>Rejected — DNC leads were still messaged</strong> — DNC governs
                outbound dialling, not iMessage, so nothing is withheld here.
              </div>
            </>
          ) : (
            <EmptyState title="No TrackDrive posts in this window" />
          )}
        </Card>

        {/* Calls by campaign — the DID-attributed inbound calls, per campaign. Read-only
            from the Arkade TrackDrive account; it gates nothing. */}
        <Card title="Calls by campaign">
          {liveLoading && !stats ? (
            <Skeleton height={120} />
          ) : stats && stats.by_campaign.length > 0 ? (
            <>
              <Breakdown
                rows={stats.by_campaign.map((c) => ({
                  label: campaignNameById.get(c.campaign_id) ?? c.campaign_id,
                  value: c.calls,
                  tone: "success" as PillTone,
                }))}
              />
              {callsStatus !== "ok" && (
                <div
                  role="note"
                  style={{
                    color:
                      callsStatus === "unavailable"
                        ? tokens.color.danger
                        : tokens.color.warning,
                    fontSize: tokens.font.size.xs,
                    marginTop: tokens.space.sm,
                  }}
                >
                  {callsStatus === "unavailable"
                    ? "TrackDrive call read unavailable — these are not confirmed zeros."
                    : "Partial — the call read hit its pagination bound, so these counts are a lower bound."}
                </div>
              )}
            </>
          ) : (
            <EmptyState title="No calls by campaign in this window" />
          )}
        </Card>

        {/* Sends by number — pure OBSERVABILITY. Bloo picks and rotates the sender
            itself (and enforces the per-number daily cap), so this panel shows only
            which numbers actually carried intros in the window: no capacity figures,
            no enforcement, nothing to configure. The numbers are OURS, not consumer
            PII, so they are shown unmasked. */}
        <Card title="Sends by number">
          {liveLoading && !stats ? (
            <Skeleton height={120} />
          ) : stats && stats.by_sender.length > 0 ? (
            <>
              <Breakdown
                rows={stats.by_sender.map((s) => ({
                  label: prettySender(s.sender_number),
                  value: sumOutcomes(s.outcomes),
                  // 🔴 THE EARLY-WARNING VIEW: accepted (the bucket total) beside the
                  // REAL delivery receipts for that number. A number whose accepted
                  // count holds flat while its delivered count falls is degrading.
                  note: `${fmtCount(s.delivered)} delivered`,
                }))}
              />
              <div
                style={{
                  color: tokens.color.fg2,
                  fontSize: tokens.font.size.xs,
                  marginTop: tokens.space.sm,
                }}
              >
                Intros accepted per sender number, with Bloo&apos;s delivery receipts
                beside each. Bloo owns rotation and the daily per-number caps — this is a
                read-out, not a control.
              </div>
            </>
          ) : (
            <EmptyState
              title="No sends recorded yet in this window."
              hint="Once the engine sends an intro, the number Bloo used appears here."
            />
          )}
        </Card>
      </div>

      {/* Recent conversations table */}
      <Card padding={0} style={{ marginBottom: tokens.space.xl }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: tokens.space.lg,
            borderBottom: `1px solid ${tokens.color.border}`,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: tokens.font.size.md }}>
              Recent conversations
            </div>
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
              {conversations ? `${conversations.length} most recent` : "loading…"}
              {conversations && conversations.length >= CONVERSATIONS_LIMIT
                ? ` (capped at ${CONVERSATIONS_LIMIT})`
                : ""}
            </div>
          </div>
        </div>

        {liveLoading && !conversations ? (
          <div style={{ padding: tokens.space.lg }}>
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
          </div>
        ) : conversations && conversations.length === 0 ? (
          <EmptyState
            title="No conversations yet"
            hint="Recent conversations appear here once the engine sends an intro."
          />
        ) : conversations ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
                whiteSpace: "nowrap",
              }}
            >
              <thead>
                <tr>
                  <Th>Contacted</Th>
                  <Th>Phone</Th>
                  <Th>Campaign</Th>
                  <Th>Tier</Th>
                  <Th>State</Th>
                  {/* 🔴 v5: the AI's verdict on the reply (bounded label, our own
                      vocabulary). THE SHADOW-REVIEW SURFACE — live classification
                      is promoted only after these are reviewed on real replies. */}
                  <Th>AI</Th>
                  <Th>TrackDrive</Th>
                  <Th>Delivered</Th>
                  <Th>Replied</Th>
                  {/* 🔴 v4: when the callback NUMBER went out as its own message
                      (message 3). Blank on a row where it never did — which, paired
                      with a "Follow-up failed" state, is the PARTIAL HANDOFF an
                      operator has to recover by hand: the lead got the response but
                      not the number. */}
                  <Th>Number sent</Th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((c, idx) => {
                  const campaign = c.campaign_id
                    ? campaignNameById.get(c.campaign_id) ?? c.campaign_id
                    : null;
                  return (
                    <tr
                      key={`${c.phone_masked}-${c.contacted_at_ms}-${idx}`}
                      style={{
                        background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                      }}
                    >
                      <Td mono>{fmtTime(c.contacted_at_ms)}</Td>
                      <Td mono>{c.phone_masked || "—"}</Td>
                      <Td>{campaign || "—"}</Td>
                      <Td>{prettyTier(c.tier)}</Td>
                      <Td>
                        <Pill tone={stateTone(c.state)}>{prettyState(c.state)}</Pill>
                      </Td>
                      {/* 🔴 v5: the LLM verdict — "—" when the AI was never consulted
                          (mode off, keyword fast path, or a pre-v5 row). In SHADOW the
                          interesting rows are verdict "engaged" beside state "Replied
                          (other)": leads live classification would have converted. */}
                      <Td>
                        {c.llm_verdict !== null ? (
                          <Pill
                            tone={
                              c.llm_verdict === "engaged"
                                ? "success"
                                : c.llm_verdict === "llm_error"
                                  ? "danger"
                                  : "neutral"
                            }
                          >
                            {c.llm_verdict}
                          </Pill>
                        ) : (
                          "—"
                        )}
                      </Td>
                      {/* Our own bounded label, straight off the row — "—" when the post
                          was never recorded (knob off, or a pre-v3 row). */}
                      <Td>{c.td_lead_outcome ?? "—"}</Td>
                      {/* The REAL Bloo delivery receipt (distinct from "accepted"). */}
                      <Td mono>{fmtTime(c.delivered_at_ms)}</Td>
                      <Td mono>{fmtTime(c.replied_at_ms)}</Td>
                      {/* 🔴 v4: message 3 — the bare callback number. `number_msg_id`'s
                          PRESENCE is the "the lead has the number" fact; when the send
                          time is missing but the id is somehow not, still show the id's
                          presence rather than an em-dash that would read as "no number". */}
                      <Td mono>
                        {c.number_sent_at_ms !== null
                          ? fmtTime(c.number_sent_at_ms)
                          : c.number_msg_id !== null
                            ? "sent"
                            : "—"}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {/* Config form — one Save persists the whole config (knobs + campaigns). */}
      <Card
        title="Operating config"
        action={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            {savedAt && (
              <span style={{ color: tokens.color.tealLight, fontSize: tokens.font.size.xs }}>
                Saved{" "}
                {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty || !form}
              style={buttonStyle("primary", saving || !dirty || !form)}
            >
              {saving ? "Saving…" : dirty ? "Save" : "Saved"}
            </button>
          </div>
        }
        style={{ marginBottom: tokens.space.xl }}
      >
        {configError && (
          <div
            role="alert"
            style={{
              marginBottom: tokens.space.lg,
              padding: `${tokens.space.sm}px ${tokens.space.md}px`,
              borderRadius: tokens.radius.sm,
              background: tokens.color.dangerBg,
              border: `1px solid ${tokens.color.dangerBorder}`,
              color: tokens.color.danger,
              fontSize: tokens.font.size.sm,
            }}
          >
            {configError}
          </div>
        )}

        {configLoading && !form ? (
          <>
            <Skeleton height={20} width={160} />
            <div style={{ height: tokens.space.md }} />
            <Skeleton height={36} />
            <div style={{ height: tokens.space.md }} />
            <Skeleton height={36} />
          </>
        ) : form ? (
          <>
            <p
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                marginTop: 0,
              }}
            >
              These knobs are the engine's real control surface — saved to the
              config the live engine reads each tick. The on/off master switch is
              at the top of the page; it (not this form) owns whether messaging is
              live.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: tokens.space.lg,
                alignItems: "start",
              }}
            >
              {/* Messages per minute */}
              <div>
                <FieldLabel
                  htmlFor="yes-msgs-per-min"
                  hint={`intros per tick — capped at ${tickCap}`}
                >
                  Messages / min
                </FieldLabel>
                <input
                  id="yes-msgs-per-min"
                  type="number"
                  min="1"
                  max={tickCap}
                  value={form.msgsPerMin}
                  onChange={(e) => update("msgsPerMin", e.target.value)}
                  style={inputStyle}
                  aria-label="Messages per minute"
                />
              </div>

              {/* Conversation TTL (hours) */}
              <div>
                <FieldLabel
                  htmlFor="yes-ttl-hours"
                  hint="hours a conversation stays open for a reply before it lapses"
                >
                  Conversation TTL (hours)
                </FieldLabel>
                <input
                  id="yes-ttl-hours"
                  type="number"
                  min="1"
                  step="1"
                  value={form.ttlHours}
                  onChange={(e) => update("ttlHours", e.target.value)}
                  style={inputStyle}
                  aria-label="Conversation TTL (hours)"
                />
              </div>
            </div>

            {/* Advanced disclosure — the timing band (guarded). */}
            <div style={{ marginTop: tokens.space.lg }}>
              <AdvancedBand
                waitMin={form.waitMin}
                maxAgeMin={form.maxAgeMin}
                bandInvalid={bandInvalid}
                onWaitChange={(v) => update("waitMin", v)}
                onMaxAgeChange={(v) => update("maxAgeMin", v)}
              />
            </div>

            {/* Sender routing */}
            <SubHeading
              title="Sender routing"
              hint="Leave all three blank (recommended) — Bloo then picks the number, rotates across the pool, keeps replies on the same number, and enforces the daily cap. From number and priority id are mutually exclusive overrides."
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: tokens.space.md,
              }}
            >
              <div>
                <FieldLabel htmlFor="yes-from-number">From number</FieldLabel>
                <input
                  id="yes-from-number"
                  type="text"
                  value={form.fromNumber}
                  onChange={(e) => update("fromNumber", e.target.value)}
                  style={inputStyle}
                  aria-label="From number"
                />
              </div>
              <div>
                <FieldLabel htmlFor="yes-priority-id">Priority id</FieldLabel>
                <input
                  id="yes-priority-id"
                  type="text"
                  value={form.priorityId}
                  onChange={(e) => update("priorityId", e.target.value)}
                  style={inputStyle}
                  aria-label="Priority id"
                />
              </div>
              <div>
                <FieldLabel
                  htmlFor="yes-channel-type"
                  hint="e.g. blooio — blank by default; setting it constrains routing"
                >
                  Channel type
                </FieldLabel>
                <input
                  id="yes-channel-type"
                  type="text"
                  value={form.channelType}
                  onChange={(e) => update("channelType", e.target.value)}
                  style={inputStyle}
                  aria-label="Channel type"
                />
              </div>
            </div>

            {/* Mirrors the server's 400 invalid_routing. */}
            {routingConflict && (
              <div
                role="alert"
                style={{
                  marginTop: tokens.space.sm,
                  color: tokens.color.danger,
                  fontSize: tokens.font.size.xs,
                }}
              >
                From number and priority id are mutually exclusive — clear one (or
                both, which lets Bloo pick and rotate the number itself).
              </div>
            )}

            {/* TrackDrive lead post — ONE knob. 🔴 Deliberately NOT hidden behind the
                Advanced disclosure: it defaults ON, it drives an upstream side-effect,
                and the TrackDrive-post card above is how an operator reads its effect.
                🔴 There is NO DNC checkbox — this is iMessage, not dialling. */}
            <SubHeading
              title="TrackDrive lead post"
              hint="TrackDrive needs the lead before the consumer calls the number we texted them. A TrackDrive failure or rejection NEVER blocks the message."
            />
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg1,
                fontSize: tokens.font.size.sm,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.trackdrivePostEnabled}
                disabled={saving}
                onChange={(e) => update("trackdrivePostEnabled", e.target.checked)}
                aria-label="Post the lead to TrackDrive before the intro"
              />
              Post the lead to TrackDrive before the intro (recommended)
            </label>

            {/* 🔴 v5 — Conversation AI: the off → shadow → classify ladder. NOT
                hidden behind Advanced: it changes what a live reply does. Shadow is
                measurement-only (verdicts recorded, keywords act); Classify ACTS on
                live traffic, so entering it is confirm-gated like the master toggle.
                "Draft" is deliberately not offered in this build. */}
            <SubHeading
              title="Conversation AI"
              hint="Classifies replies the keyword lists can't (e.g. answers to a step question) with claude-sonnet-5. Shadow = record verdicts only, keywords still decide. Live = an 'engaged' verdict sends the follow-up + number; any AI failure falls back to keywords. STOP handling is always keyword-based and runs first."
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.space.md,
                flexWrap: "wrap",
              }}
            >
              <select
                value={form.llmMode}
                disabled={saving}
                aria-label="Conversation AI mode"
                style={{ ...inputStyle, width: "auto", minWidth: 220 }}
                onChange={(e) => {
                  const next = e.target.value as YesLlmMode;
                  // 🔴 The confirm gate on entering the LIVE mode (master-toggle
                  // precedent): shadow → classify changes real consumer messaging.
                  if (
                    next === "classify" &&
                    !window.confirm(
                      "Live classification: an 'engaged' AI verdict will send the follow-up and the callback number to real consumers. The recommendation is to review shadow verdicts on 200+ real replies first. Turn on live classification?",
                    )
                  ) {
                    return;
                  }
                  update("llmMode", next);
                }}
              >
                <option value="off">Off — keywords only (v4 behaviour)</option>
                <option value="shadow">Shadow — record AI verdicts, act on keywords</option>
                <option value="classify">Live — act on AI verdicts (confirm)</option>
              </select>
              {form.llmMode !== "off" && (
                <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
                  {form.llmMode === "shadow"
                    ? "Verdicts appear on Recent conversations — review them there before going live."
                    : "Acting on AI verdicts. Failures fall back to keywords; STOP stays keyword-decided."}
                </span>
              )}
            </div>

            {/* Reply keywords */}
            <SubHeading
              title="Reply keywords"
              hint="Comma- or line-separated. A reply matching an affirmative keyword converts (sends the follow-up); a stop keyword opts the lead out."
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: tokens.space.md,
              }}
            >
              <KeywordsField
                id="yes-affirmative-keywords"
                label="Affirmative keywords"
                hint="yes, y, yeah, sure, ok…"
                value={form.affirmativeKeywords}
                onChange={(v) => update("affirmativeKeywords", v)}
                disabled={saving}
              />
              <KeywordsField
                id="yes-stop-keywords"
                label="Stop keywords"
                hint="stop, unsubscribe, cancel, quit…"
                value={form.stopKeywords}
                onChange={(v) => update("stopKeywords", v)}
                disabled={saving}
              />
            </div>

            {/* Campaigns editor */}
            <SubHeading
              title="Campaigns"
              hint="Per debt threshold: the intro message + the follow-up (which holds the callback phone number the yes-reply sends). A lead with no matching campaign is skipped (no_campaign)."
            />
            <CampaignsEditor
              value={form.campaigns}
              onChange={(next) => update("campaigns", next)}
              disabled={saving}
            />
          </>
        ) : null}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Master on/off toggle — prominent status + a deliberate confirm to enable.
// Off is immediate (no confirm); On reveals the warning + confirm buttons.
// ---------------------------------------------------------------------------

function MasterToggle({
  loading,
  enabled,
  healthHint,
  toggling,
  confirming,
  toggleError,
  onRequestEnable,
  onConfirmEnable,
  onCancelEnable,
  onDisable,
}: {
  loading: boolean;
  enabled: boolean;
  healthHint: string;
  toggling: boolean;
  confirming: boolean;
  toggleError: string | null;
  onRequestEnable: () => void;
  onConfirmEnable: () => void;
  onCancelEnable: () => void;
  onDisable: () => void;
}) {
  return (
    <Card
      style={{
        marginBottom: tokens.space.xl,
        borderColor: enabled ? "rgba(0,212,170,0.28)" : tokens.color.border,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: tokens.space.lg,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space.md,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: tokens.font.size.lg,
                color: tokens.color.fg0,
              }}
            >
              Yes engine
            </span>
            {loading ? (
              <Pill tone="neutral">…</Pill>
            ) : enabled ? (
              <Pill tone="success">On</Pill>
            ) : (
              <Pill tone="neutral">Off</Pill>
            )}
          </div>
          <p
            style={{
              margin: `${tokens.space.sm}px 0 0`,
              color: tokens.color.fg1,
              fontSize: tokens.font.size.sm,
            }}
          >
            {healthHint}
          </p>
        </div>

        <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
          {toggling ? (
            <button type="button" disabled style={buttonStyle("ghost", true)}>
              Working…
            </button>
          ) : enabled ? (
            <button
              type="button"
              onClick={onDisable}
              aria-label="Turn Yes off"
              style={buttonStyle("danger")}
            >
              Turn Off
            </button>
          ) : confirming ? (
            <>
              <button
                type="button"
                onClick={onConfirmEnable}
                aria-label="Confirm — start messaging live leads"
                style={buttonStyle("primary")}
              >
                Yes, start messaging live
              </button>
              <button type="button" onClick={onCancelEnable} style={buttonStyle("ghost")}>
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onRequestEnable}
              aria-label="Turn Yes on"
              disabled={loading}
              style={buttonStyle("primary", loading)}
            >
              Turn On
            </button>
          )}
        </div>
      </div>

      {confirming && !enabled && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.lg,
            padding: `${tokens.space.md}px ${tokens.space.lg}px`,
            borderRadius: tokens.radius.md,
            background: tokens.color.dangerBg,
            border: `1px solid ${tokens.color.dangerBorder}`,
            color: tokens.color.danger,
            fontSize: tokens.font.size.sm,
          }}
        >
          <strong>Are you sure — this starts messaging live leads over iMessage.</strong>{" "}
          Turning the engine on sends real intros to qualifying leads on the next
          tick (real conversations, real callback exposure). Leads sold to an
          exclusive buyer are withheld; a yes-reply routes the lead to the
          campaign's callback. Off is immediate; on is deliberate.
        </div>
      )}

      {toggleError && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.md,
            color: tokens.color.danger,
            fontSize: tokens.font.size.sm,
          }}
        >
          {toggleError}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Advanced disclosure — the eligibility band (wait / max_age) in MINUTES, with
// the client-side max_age>wait guard. Collapsed by default so the common config
// stays uncluttered.
// ---------------------------------------------------------------------------

function AdvancedBand({
  waitMin,
  maxAgeMin,
  bandInvalid,
  onWaitChange,
  onMaxAgeChange,
}: {
  waitMin: string;
  maxAgeMin: string;
  bandInvalid: boolean;
  onWaitChange: (v: string) => void;
  onMaxAgeChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.md,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: tokens.space.sm,
          padding: `${tokens.space.md}px ${tokens.space.lg}px`,
          background: "transparent",
          border: "none",
          color: tokens.color.fg0,
          fontSize: tokens.font.size.base,
          fontWeight: 600,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span>Advanced — timing band</span>
        <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div
          style={{
            padding: `0 ${tokens.space.lg}px ${tokens.space.lg}px`,
            display: "flex",
            flexDirection: "column",
            gap: tokens.space.md,
          }}
        >
          <div
            role="note"
            style={{
              padding: `${tokens.space.sm}px ${tokens.space.md}px`,
              borderRadius: tokens.radius.sm,
              background: "rgba(212,168,83,0.12)",
              border: "1px solid rgba(212,168,83,0.40)",
              color: tokens.color.warning,
              fontSize: tokens.font.size.xs,
            }}
          >
            The band is a narrow window after the form-fill: <strong>wait</strong>{" "}
            lets the lead settle (and the exclusive-sale signal arrive) before the
            intro; <strong>max age</strong> keeps a stale lead from being messaged
            long after it went cold. Max age must exceed wait or the band is empty.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.lg,
            }}
          >
            <div>
              <FieldLabel
                htmlFor="yes-wait-min"
                hint="minutes to wait after the form-fill before a lead is eligible (settle window)"
              >
                Wait (minutes)
              </FieldLabel>
              <input
                id="yes-wait-min"
                type="number"
                min="1"
                step="1"
                value={waitMin}
                onChange={(e) => onWaitChange(e.target.value)}
                style={inputStyle}
                aria-label="Wait (minutes)"
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="yes-max-age-min"
                hint="minutes after the form-fill past which a lead is too stale to message"
              >
                Max age (minutes)
              </FieldLabel>
              <input
                id="yes-max-age-min"
                type="number"
                min="1"
                step="1"
                value={maxAgeMin}
                onChange={(e) => onMaxAgeChange(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor: bandInvalid
                    ? tokens.color.dangerBorder
                    : tokens.color.borderMid,
                }}
                aria-label="Max age (minutes)"
              />
            </div>
          </div>

          {bandInvalid && (
            <div
              role="alert"
              style={{ color: tokens.color.danger, fontSize: tokens.font.size.xs }}
            >
              Max age must be greater than wait — the band is empty as entered, so
              the engine would message nobody. Fix this before saving.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Keywords field — a labelled textarea for a comma/newline-separated list.
// ---------------------------------------------------------------------------

function KeywordsField({
  id,
  label,
  hint,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id} {...(hint ? { hint } : {})}>
        {label}
      </FieldLabel>
      <textarea
        id={id}
        value={value}
        disabled={disabled}
        rows={2}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...inputStyle,
          resize: "vertical",
          fontFamily: tokens.font.mono,
          fontSize: tokens.font.size.sm,
        }}
        aria-label={label}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaigns editor — add/remove per-threshold campaigns. Each carries a stable
// id (the ledger + by-campaign stats key on it), a name, an enabled flag, the
// minimum debt threshold (dollars), and the intro + follow-up copy (the follow-up
// holds the callback phone number the yes-reply sends). Clone of LeadSales's
// DestinationsEditor.
// ---------------------------------------------------------------------------

function CampaignsEditor({
  value,
  onChange,
  disabled,
}: {
  value: CampaignDraft[];
  onChange: (next: CampaignDraft[]) => void;
  disabled: boolean;
}) {
  const add = (): void => onChange([...value, newCampaignDraft()]);
  const remove = (id: string): void => onChange(value.filter((c) => c.id !== id));
  const patch = (id: string, p: Partial<CampaignDraft>): void =>
    onChange(value.map((c) => (c.id === id ? { ...c, ...p } : c)));

  const textareaStyle: CSSProperties = { ...inputStyle, resize: "vertical" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
      {value.length === 0 && (
        <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
          No campaigns yet — add one to start messaging leads. With no matching
          campaign a lead is skipped (no_campaign).
        </div>
      )}

      {value.map((c, idx) => (
        <div
          key={c.id}
          style={{
            border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.md,
            padding: tokens.space.lg,
            display: "flex",
            flexDirection: "column",
            gap: tokens.space.md,
            background: tokens.color.bg1,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: tokens.space.md,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
              <span
                style={{
                  color: tokens.color.fg2,
                  fontSize: tokens.font.size.xs,
                  fontFamily: tokens.font.mono,
                }}
              >
                #{idx + 1}
              </span>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: tokens.color.fg1,
                  fontSize: tokens.font.size.sm,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={c.enabled}
                  disabled={disabled}
                  onChange={(e) => patch(c.id, { enabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>
            <button
              type="button"
              onClick={() => remove(c.id)}
              disabled={disabled}
              style={buttonStyle("ghost", disabled)}
            >
              Remove
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: tokens.space.md,
            }}
          >
            <div>
              <FieldLabel htmlFor={`yes-camp-name-${c.id}`}>Name</FieldLabel>
              <input
                id={`yes-camp-name-${c.id}`}
                type="text"
                value={c.name}
                disabled={disabled}
                onChange={(e) => patch(c.id, { name: e.target.value })}
                style={inputStyle}
                aria-label="Campaign name"
              />
            </div>
            <div>
              <FieldLabel
                htmlFor={`yes-camp-mindebt-${c.id}`}
                hint="the lead's debt must be at or above this to match"
              >
                Minimum debt ($)
              </FieldLabel>
              <input
                id={`yes-camp-mindebt-${c.id}`}
                type="number"
                min="0"
                step="1"
                value={c.minDebt}
                disabled={disabled}
                placeholder="10000"
                onChange={(e) => patch(c.id, { minDebt: e.target.value })}
                style={inputStyle}
                aria-label="Campaign minimum debt"
              />
            </div>
            <div>
              {/* 🔴 v4: this number is now SENT AS ITS OWN MESSAGE (message 3), right
                  after the follow-up, exactly as typed — that is what makes it
                  tappable in iMessage. The hint says so explicitly so an operator does
                  NOT also inline it via {p} in the follow-up copy and send it twice. */}
              <FieldLabel
                htmlFor={`yes-camp-callback-${c.id}`}
                hint="sent as its OWN message right after the follow-up (that's what makes it tappable) — typed exactly as you want it to appear. Don't also put it in the follow-up copy."
              >
                Callback number — sent as its own message
              </FieldLabel>
              <input
                id={`yes-camp-callback-${c.id}`}
                type="text"
                value={c.callbackNumber}
                disabled={disabled}
                placeholder="1-888-533-1148"
                onChange={(e) => patch(c.id, { callbackNumber: e.target.value })}
                style={inputStyle}
                aria-label="Campaign callback number"
              />
            </div>
          </div>

          <div>
            <FieldLabel htmlFor={`yes-camp-intro-${c.id}`}>Intro copy</FieldLabel>
            <textarea
              id={`yes-camp-intro-${c.id}`}
              value={c.intro_copy}
              disabled={disabled}
              rows={2}
              onChange={(e) => patch(c.id, { intro_copy: e.target.value })}
              style={textareaStyle}
              aria-label="Campaign intro copy"
            />
            <TagHint />
          </div>

          {/* 🔴 v5 — timed steps: nudges sent AFTER the intro while the lead hasn't
              replied ("wait 1 min → quick q before I assign your specialist…").
              A reply cancels pending steps; a step >30 min past due (quiet hours,
              an outage) is dropped rather than sent stale. Max 5. */}
          <div>
            <FieldLabel
              htmlFor={`yes-camp-steps-${c.id}`}
              hint="each sends the given minutes AFTER the previous message, only while the lead hasn't replied. A reply cancels the rest; late steps (30+ min) are dropped, never sent stale."
            >
              Timed steps (before the reply)
            </FieldLabel>
            <div
              id={`yes-camp-steps-${c.id}`}
              style={{ display: "flex", flexDirection: "column", gap: tokens.space.sm }}
            >
              {c.steps.map((s, j) => (
                <div
                  key={s.key}
                  style={{
                    display: "flex",
                    gap: tokens.space.sm,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      color: tokens.color.fg2,
                      fontSize: tokens.font.size.xs,
                      fontFamily: tokens.font.mono,
                      paddingTop: 8,
                    }}
                  >
                    {j + 2}.
                  </span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={s.delayMin}
                    disabled={disabled}
                    onChange={(e) =>
                      patch(c.id, {
                        steps: c.steps.map((x) =>
                          x.key === s.key ? { ...x, delayMin: e.target.value } : x,
                        ),
                      })
                    }
                    style={{ ...inputStyle, width: 88 }}
                    aria-label={`Step ${j + 1} wait minutes`}
                    title="minutes after the previous message"
                  />
                  <span
                    style={{
                      color: tokens.color.fg2,
                      fontSize: tokens.font.size.xs,
                      paddingTop: 8,
                    }}
                  >
                    min →
                  </span>
                  <textarea
                    value={s.copy}
                    disabled={disabled}
                    rows={1}
                    placeholder="Quick q before I assign your specialist — is it mostly credit cards? Or a mix of stuff?"
                    onChange={(e) =>
                      patch(c.id, {
                        steps: c.steps.map((x) =>
                          x.key === s.key ? { ...x, copy: e.target.value } : x,
                        ),
                      })
                    }
                    style={{ ...textareaStyle, flex: 1, minWidth: 220 }}
                    aria-label={`Step ${j + 1} copy`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      patch(c.id, { steps: c.steps.filter((x) => x.key !== s.key) })
                    }
                    disabled={disabled}
                    style={buttonStyle("ghost", disabled)}
                    aria-label={`Remove step ${j + 1}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {c.steps.length < 5 && (
                <div>
                  <button
                    type="button"
                    onClick={() =>
                      patch(c.id, { steps: [...c.steps, newStepDraft()] })
                    }
                    disabled={disabled}
                    style={buttonStyle("subtle", disabled)}
                  >
                    + Add timed step
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            {/* 🔴 v4: the follow-up NO LONGER needs to carry the number — the callback
                number follows immediately as its own message. */}
            <FieldLabel
              htmlFor={`yes-camp-followup-${c.id}`}
              hint="sent when the lead replies yes — the callback number follows immediately as a separate message, so this copy doesn't need it"
            >
              Follow-up copy
            </FieldLabel>
            <textarea
              id={`yes-camp-followup-${c.id}`}
              value={c.followup_copy}
              disabled={disabled}
              rows={2}
              onChange={(e) => patch(c.id, { followup_copy: e.target.value })}
              style={textareaStyle}
              aria-label="Campaign follow-up copy"
            />
            <TagHint />
          </div>

          {/* 🔴 v5 — the number delay ("wait 1 min, send them the phone number").
              0/blank = immediately in the same webhook (v4). >0 = the dispatcher
              tick sends it, ±1 min granularity. */}
          <div>
            <FieldLabel
              htmlFor={`yes-camp-numberdelay-${c.id}`}
              hint="minutes between the follow-up and the number message. 0 = immediately (v4). Timing is ±1 minute (the engine's tick). A STOP during the wait cancels the number."
            >
              Number delay (min)
            </FieldLabel>
            <input
              id={`yes-camp-numberdelay-${c.id}`}
              type="number"
              min="0"
              step="1"
              value={c.numberDelayMin}
              disabled={disabled}
              placeholder="0"
              onChange={(e) => patch(c.id, { numberDelayMin: e.target.value })}
              style={{ ...inputStyle, width: 120 }}
              aria-label="Minutes before the number message"
            />
          </div>
        </div>
      ))}

      <div>
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          style={buttonStyle("subtle", disabled)}
        >
          + Add campaign
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TagHint — the merge-tag vocabulary, shown under each copy field. Same wording as
// rtb-api's domain/yes_tags.ts:YES_TAG_HINT (copied — admin-web cannot import
// rtb-api) so the UI can never promise a tag the renderer does not substitute.
// ---------------------------------------------------------------------------

function TagHint() {
  return (
    <div
      style={{
        color: tokens.color.fg2,
        fontSize: tokens.font.size.xs,
        marginTop: 4,
        fontFamily: tokens.font.mono,
      }}
    >
      {YES_TAG_HINT}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubHeading — a divider + label for a config sub-section.
// ---------------------------------------------------------------------------

function SubHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div
      style={{
        marginTop: tokens.space.xl,
        marginBottom: tokens.space.md,
        paddingTop: tokens.space.lg,
        borderTop: `1px solid ${tokens.color.border}`,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: tokens.font.size.md, color: tokens.color.fg0 }}>
        {title}
      </div>
      {hint && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginTop: 2,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdown — a compact label → count list used for the funnel, skip reasons,
// and by-campaign / by-tier cards. An optional tone tints the count.
// ---------------------------------------------------------------------------

function Breakdown({
  rows,
}: {
  rows: Array<{ label: string; value: number; tone?: PillTone; note?: string }>;
}) {
  const toneColor: Record<PillTone, string> = {
    neutral: tokens.color.fg0,
    success: tokens.color.tealLight,
    warning: tokens.color.warning,
    danger: tokens.color.danger,
  };
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {rows.map((r, i) => (
        <div
          key={r.label}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.space.md,
            padding: `${tokens.space.sm}px 0`,
            borderBottom:
              i < rows.length - 1 ? `1px solid ${tokens.color.border}` : "none",
          }}
        >
          <span style={{ color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
            {r.label}
          </span>
          {/* An optional secondary figure, kept in its OWN element (never nested inside
              the label or the value) so each stays independently readable. */}
          {r.note !== undefined && (
            <span
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.xs,
                fontFamily: tokens.font.mono,
                marginLeft: "auto",
                marginRight: tokens.space.md,
              }}
            >
              {r.note}
            </span>
          )}
          <span
            style={{
              fontFamily: tokens.font.mono,
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              color: r.tone ? toneColor[r.tone] : tokens.color.fg0,
            }}
          >
            {fmtCount(r.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared table + skeleton atoms (mirrors LeadSales / Dialer).
// ---------------------------------------------------------------------------

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
      }}
    >
      {children}
    </td>
  );
}
